import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";

/**
 * One entry of the server-driven error-kind catalogue. Mirrors the
 * Pydantic shape on `controller/api/routers/reference.py::ErrorKindRef`
 * which is itself a one-to-one projection of an entry in
 * `controller/controller/data/error_rules.yaml`.
 *
 * `severity` decides which state-machine arm the reconciler routes
 * this kind to (`transient` = self-healing retry; `attention_external`
 * = operator-visible, fix is outside backstage; `attention_spec` =
 * operator-visible, fix is a pool spec edit). The WebUI uses it to
 * tone the chip (yellow / red) and to pick the correct call-to-action
 * on the callout ("we'll keep trying" vs "open Pool Settings").
 */
export interface ErrorKindRef {
  kind: string;
  severity: "transient" | "attention_external" | "attention_spec";
  label: string;
  reason: string | null;
  hint: string | null;
  has_needle: boolean;
}

export interface ErrorKindReference {
  error_kinds: ErrorKindRef[];
}

/**
 * Shared React-Query handle for `GET /reference/error-kinds`.
 *
 * Same caching shape as {@link usePhaseReference} — the catalogue is
 * process-static (built from `error_rules.yaml` at controller boot)
 * so a 30-minute stale time costs nothing while saving a network
 * round-trip on every PoolReplicas mount.
 */
export function useErrorKindReference() {
  return useQuery<ErrorKindReference>({
    queryKey: ["reference", "error-kinds"],
    queryFn: () => api.get<ErrorKindReference>("/reference/error-kinds"),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  });
}

/**
 * Lookup helper used by both the provisioning-error callout and the
 * per-replica failure chip on the Pool Replicas tab. Returns `null`
 * for falsy / unknown kinds; the caller decides how to fall back
 * (usually by rendering the raw kind string so we never go blank
 * during a deploy that adds a new kind before the WebUI bundle
 * catches up).
 */
export function useErrorKindRef(
  kind: string | undefined | null,
): ErrorKindRef | null {
  const ref = useErrorKindReference();
  if (!kind) return null;
  return ref.data?.error_kinds.find((k) => k.kind === kind) ?? null;
}
